import dayjs from "dayjs";
import { LOB_MODULE, DB_MODULE } from "../constants";
import { isSameModule, isDbModule, moduleToFallback, normalizeModule } from "./module";

export const PERIOD_STATUS_LIVE = "live";
export const PERIOD_STATUS_DRAFT = "draft";
export const PERIOD_STATUS_ARCHIVED = "archived";
export const OPEN_PERIOD_LIMIT = 2;
export const PERIOD_OPEN_LIMIT_TRANSLATION_KEY = "message.error.period_open_limit_reached";

// Regellaufzeit per module, the default duration a new period is proposed with.
export const MODULE_DEFAULT_YEARS = {
  [LOB_MODULE]: 1,
  [DB_MODULE]: 3,
};

// The timeline shows a window of the chain rather than everything ever created.
export const LANE_WINDOW = 5;

export const moduleOf = (period) => normalizeModule(period?.module, null)
  ?? normalizeModule(period?.synced_modules?.[0], null);

export const defaultYearsFor = (module) => MODULE_DEFAULT_YEARS[normalizeModule(module)] ?? 1;

export const toDay = (value) => (value ? dayjs(value, "YYYY-MM-DD") : null);

export const formatDay = (value) => {
  const day = toDay(value);
  return day && day.isValid() ? day.format("DD.MM.YYYY") : "";
};

export const toIso = (day) => (day && day.isValid() ? day.format("YYYY-MM-DD") : "");

export const timeOf = (value) => {
  const day = toDay(value);
  return day && day.isValid() ? day.valueOf() : NaN;
};

export const daysBetween = (from, to) => toDay(to).diff(toDay(from), "day");

export const monthsBetween = (from, to) => toDay(to).diff(toDay(from), "month") + 1;

export const hasRange = (period) => !!(period?.evaluation_period_start_date && period?.evaluation_period_end_date);

const dropdownBucketOf = (period) => {
  const module = moduleOf(period);
  if (!module) return 2;

  return isDbModule(module) ? 1 : 0;
};

/**
 * Dropdown order: module group, then newest evaluation period first, with
 * undated periods at the bottom of their own group. Ties return 0 so the
 * stable sort keeps the order the backend sent.
 */
export const sortPeriodsForDropdown = (periods) => [...(periods || [])].sort((left, right) => {
  const bucket = dropdownBucketOf(left) - dropdownBucketOf(right);
  if (bucket !== 0) return bucket;

  const leftStart = timeOf(left?.evaluation_period_start_date);
  const rightStart = timeOf(right?.evaluation_period_start_date);
  const leftUndated = Number.isNaN(leftStart);
  const rightUndated = Number.isNaN(rightStart);

  if (leftUndated || rightUndated) {
    return leftUndated === rightUndated ? 0 : (leftUndated ? 1 : -1);
  }

  return rightStart - leftStart;
});

/** Periods of one module, oldest first. A period without dates cannot be placed. */
export const chainOf = (periods, module) => (periods || [])
  .filter((p) => isSameModule(moduleOf(p), module) && hasRange(p))
  .sort((a, b) => timeOf(a.evaluation_period_start_date) - timeOf(b.evaluation_period_start_date));

export const previousOf = (periods, period) => {
  const chain = chainOf(periods, moduleOf(period))
    .filter((p) => p.id !== period?.id && timeOf(p.evaluation_period_end_date) < timeOf(period?.evaluation_period_start_date));
  return chain[chain.length - 1] || null;
};

export const nextOf = (periods, period) => chainOf(periods, moduleOf(period))
  .find((p) => p.id !== period?.id && timeOf(p.evaluation_period_start_date) > timeOf(period?.evaluation_period_end_date)) || null;

export const latestIn = (periods, module) => {
  const chain = chainOf(periods, module);
  return chain[chain.length - 1] || null;
};

export const draftIn = (periods, module) => chainOf(periods, module)
  .find((p) => p.status === PERIOD_STATUS_DRAFT) || null;

/** Live and draft periods consume the shared open-period capacity per module. */
export const openPeriodsIn = (periods, module) => (periods || [])
  .filter((period) => isSameModule(moduleOf(period), module)
    && [PERIOD_STATUS_LIVE, PERIOD_STATUS_DRAFT].includes(period?.status));

export const openPeriodLimitReachedIn = (periods, module) => (
  openPeriodsIn(periods, module).length >= OPEN_PERIOD_LIMIT
);

export const openPeriodLimitReplacementsFor = (periods, module) => ({
  module: moduleToFallback(module),
  periods: openPeriodsIn(periods, module).map((period) => period.name).join(", "),
});

/** Keep the complete actionable conflict message without changing global error truncation. */
export const preserveOpenPeriodLimitErrorMessage = (error, formattedError) => {
  const responseData = error?.response?.data;

  if (responseData?.translation_key !== PERIOD_OPEN_LIMIT_TRANSLATION_KEY
    || typeof responseData?.message !== "string") {
    return formattedError;
  }

  return {
    ...formattedError,
    message: responseData.message,
  };
};

/**
 * Open periods of one module that lack an evaluation range. They cannot be
 * placed on the timeline, so they are listed under their lane instead, where
 * they can be opened and given a range (LIX-1762). Archived ones stay hidden,
 * the same as archived periods leave the lanes.
 */
export const undatedIn = (periods, module) => (periods || [])
  .filter((p) => isSameModule(moduleOf(p), module)
    && !hasRange(p)
    && p.status !== PERIOD_STATUS_ARCHIVED);

/** Bars on a lane: active and draft only, archived stay in the Abfolge. */
export const laneOf = (periods, module) => {
  const visible = chainOf(periods, module).filter((p) => p.status !== PERIOD_STATUS_ARCHIVED);
  return visible.slice(Math.max(0, visible.length - LANE_WINDOW));
};

/** Any period of the module counts here, including one without dates. */
export const hasAnyPeriodIn = (periods, module) => (periods || [])
  .some((p) => isSameModule(moduleOf(p), module));

/**
 * The proposal that closes a lane: starts the day the last period ends. A lane
 * with no period at all (a module licensed after the fact) proposes its first
 * period from the start of the current year, the only case the API accepts
 * without a source. A lane holding only undated periods proposes nothing,
 * because those periods already count as the module's first.
 */
export const ghostFor = (periods, module) => {
  const last = latestIn(periods, module);
  if (!last) {
    if (hasAnyPeriodIn(periods, module)) return null;

    const { start, end, name } = suggestedRangeFor(null, module);

    return {
      module,
      ghost: true,
      first: true,
      name,
      evaluation_period_start_date: start,
      evaluation_period_end_date: end,
    };
  }

  const start = toDay(last.evaluation_period_end_date).add(1, "day");
  const years = defaultYearsFor(module);
  const end = dayjs(new Date(start.year() + years - 1, 11, 31));

  return {
    module,
    ghost: true,
    name: years === 1 ? String(start.year()) : `${start.year()} - ${end.year()}`,
    evaluation_period_start_date: toIso(start),
    evaluation_period_end_date: toIso(end),
  };
};

/** Start and duration a new period is proposed with, derived from its source. */
export const suggestedRangeFor = (sourcePeriod, module) => {
  const start = sourcePeriod
    ? toDay(sourcePeriod.evaluation_period_end_date).add(1, "day")
    : dayjs().startOf("year");
  const years = defaultYearsFor(module);
  const end = dayjs(new Date(start.year() + years - 1, 11, 31));

  return {
    start: toIso(start),
    end: toIso(end),
    name: years === 1 ? String(start.year()) : `${start.year()} - ${end.year()}`,
  };
};

export const isDefaultPeriod = (period) => !!period?.is_active_period;

/**
 * Why an action is not offered. The reasons are shown gathered in the detail
 * area rather than as greyed menu entries, so each one has to read as a
 * sentence on its own.
 */
export const lockReasonKeyFor = (period, action, { currentPeriodName, periods } = {}) => {
  if (isDefaultPeriod(period) && ["set_archived", "set_draft", "set_deleted"].includes(action)) {
    return "period_management.lock.is_default_period";
  }
  // Archiving or deleting the period the user is working in would pull the
  // ground out from under the running session.
  if (currentPeriodName && period?.name === currentPeriodName
    && ["set_archived", "set_deleted"].includes(action)) {
    return "period_management.lock.is_current_period";
  }
  if (action === "set_deleted" && period?.status !== PERIOD_STATUS_DRAFT) {
    return "period_management.lock.only_drafts_deletable";
  }
  if (action === "set_deleted" && period?.has_evaluations) {
    return "period_management.lock.has_evaluations";
  }
  if (action === "set_live" && period?.status === PERIOD_STATUS_ARCHIVED
    && openPeriodLimitReachedIn(periods, moduleOf(period))) {
    return "period_management.lock.open_limit_reached";
  }
  return null;
};

export const isLocked = (period, action, context) => lockReasonKeyFor(period, action, context) !== null;

/** Same reason, one line: "Zurück zu Entwurf, Archivieren und Löschen: solange …" */
export const groupedLocksFor = (period, actions, labelOf, joinWord, context) => {
  const byReason = new Map();

  actions.forEach((action) => {
    const reasonKey = lockReasonKeyFor(period, action, context);
    if (!reasonKey) return;
    if (!byReason.has(reasonKey)) byReason.set(reasonKey, []);
    byReason.get(reasonKey).push(labelOf(action));
  });

  return Array.from(byReason.entries()).map(([reasonKey, labels]) => ({
    reasonKey,
    what: labels.length > 1
      ? `${labels.slice(0, -1).join(", ")} ${joinWord} ${labels[labels.length - 1]}`
      : labels[0],
  }));
};

export const isModuleState = (module) => isDbModule(module);
